import { useLayoutEffect, useRef, type ReactNode } from "react";
import type { FulfillmentOrderLine, FulfillmentWorkspace } from "../../types";
import { CONFIRMATION_CATEGORIES, confirmationCategoryForLine } from "../../service/confirmationEditorView";
import { sumActiveFulfillmentLineAmounts, subtractFulfillmentAmounts } from "../../service/fulfillmentOrderEditorService";
import { formatConfirmationDecimal } from "../../service/confirmationDecimalDisplay";

const money = (value: string | number) => formatConfirmationDecimal(value, true);

/** 项目、真实来源版本和当前费用汇总，缺失来源不填充演示数据。 */
export function ConfirmationSidebar({ workspace, lines, currentAmount, children }: { workspace: FulfillmentWorkspace; lines: FulfillmentOrderLine[]; currentAmount: string; children?: ReactNode }) {
  const sidebarRef = useRef<HTMLElement>(null);
  useLayoutEffect(() => {
    const sidebar = sidebarRef.current;
    if (!sidebar) return;
    let frame = 0;
    const updateHeight = () => {
      frame = 0;
      if (window.innerWidth <= 820) {
        sidebar.style.removeProperty("--confirmation-sidebar-height");
        return;
      }
      const inset = Number.parseFloat(window.getComputedStyle(sidebar).top) || 0;
      const top = Math.max(inset, sidebar.getBoundingClientRect().top);
      const height = `${Math.max(0, Math.floor(window.innerHeight - top - inset))}px`;
      if (sidebar.style.getPropertyValue("--confirmation-sidebar-height") !== height) {
        sidebar.style.setProperty("--confirmation-sidebar-height", height);
      }
    };
    const scheduleUpdate = () => {
      if (!frame) frame = window.requestAnimationFrame(updateHeight);
    };
    updateHeight();
    // 页头换行会改变可用空间；不观察侧栏或由它撑高的父容器，避免高度反馈循环。
    const heading = sidebar.closest(".confirmation-editor-theme")?.querySelector(".confirmation-workspace-header");
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(scheduleUpdate);
    if (heading) observer?.observe(heading);
    document.addEventListener("scroll", scheduleUpdate, { capture: true, passive: true });
    window.addEventListener("resize", scheduleUpdate, { passive: true });
    return () => {
      window.cancelAnimationFrame(frame);
      observer?.disconnect();
      document.removeEventListener("scroll", scheduleUpdate, true);
      window.removeEventListener("resize", scheduleUpdate);
      sidebar.style.removeProperty("--confirmation-sidebar-height");
    };
  }, []);
  const hasBudget = Boolean(workspace.sourceBudgetOrderId && workspace.sourceBudgetVersionNo > 0);
  const difference = subtractFulfillmentAmounts(currentAmount, workspace.sourceBudgetTotalAmount || "0");
  const peopleCounts = workspace.peopleCounts;
  return <aside ref={sidebarRef} className="confirmation-sidebar" aria-label="项目与成本参考">
    <details className="confirmation-sidebar-panel confirmation-project-panel" open>
      <summary><h3>项目行程</h3></summary>
      <strong className="confirmation-project-title">{workspace.itineraryTitle}</strong>
      <dl>
        <div><dt>出行日期</dt><dd className="confirmation-date-range" title={`${workspace.serviceDateFrom} 至 ${workspace.serviceDateTo}`}><span>{workspace.serviceDateFrom?.slice(5, 10) || "待设置"}</span><span>至 {workspace.serviceDateTo?.slice(5, 10) || "待设置"}</span></dd></div>
        <div><dt>统一人数</dt><dd className="confirmation-people-counts"><span><span>学生</span>{" "}<span>{peopleCounts?.studentCount ?? "未填写"}</span></span><span><span>老师</span>{" "}<span>{peopleCounts?.teacherCount ?? "未填写"}</span></span></dd></div>
        <div><dt>司陪人数</dt><dd className="confirmation-people-counts"><span><span>导游</span>{" "}<span>{peopleCounts?.guideCount ?? "未填写"}</span></span><span><span>司机</span>{" "}<span>{peopleCounts?.driverCount ?? "未填写"}</span></span></dd></div>
      </dl>
    </details>
    <details className="confirmation-sidebar-panel" open>
      <summary><h3>核价快照</h3>{hasBudget ? <span className="confirmation-baseline-tag">已锁定基线</span> : null}</summary>
      {hasBudget ? <>
        <strong>核价 V{workspace.sourceBudgetVersionNo} · {workspace.sourceBudgetUpdatedAt?.replace("T", " ").slice(0, 16) || "—"}</strong>
        <p>生成确认件后独立调整，核价更新不会自动覆盖。</p>
        <a href={`/quote/budgets/${encodeURIComponent(workspace.sourceBudgetOrderId)}`} target="_blank" rel="noreferrer">查看来源核价当前内容</a>
      </> : <><strong>独立确认件</strong><p>当前费用由人工录入或历史复制。</p><p>业务版本 {workspace.currentVersionNo ? `V${workspace.currentVersionNo}` : "尚未建立"}</p></>}
    </details>
    <details className="confirmation-sidebar-panel confirmation-cost-panel" open>
      <summary><h3>成本对比</h3></summary>
      <dl>
        <div><dt>核价成本</dt><dd>{hasBudget ? `¥${money(workspace.sourceBudgetTotalAmount)}` : "未关联"}</dd></div>
        <div><dt>确认成本</dt><dd>¥{money(currentAmount)}</dd></div>
        {hasBudget ? <div className="confirmation-cost-difference"><dt title={workspace.budgetImport?.issues.length ? "确认成本减去核价成本，包含未导入费用的差额；未导入明细可在检查概览查看。" : "确认成本减去核价成本。"}>当前调整</dt><dd>{Number(difference) < 0 ? "-" : Number(difference) > 0 ? "+" : ""}¥{money(difference).replace(/^-/, "")}</dd></div> : null}
      </dl>
      <div className="confirmation-sidebar-categories">{CONFIRMATION_CATEGORIES.map(category => <div key={category.key}><span>{category.label}</span><strong>¥{money(sumActiveFulfillmentLineAmounts(lines.filter(line => confirmationCategoryForLine(line) === category.key)))}</strong></div>)}</div>
    </details>
    {children}
  </aside>;
}
